import { useAtom } from "jotai";
import {
  createContext,
  useCallback,
  useContext,
  type ReactNode,
} from "react";
import { stopSessionStreams } from "./streamSelectors";
import { streamsAtom, type StreamingState } from "./streamStore";

const streamMatchesSession = (
  stream: StreamingState,
  sessionId: string | undefined,
): boolean =>
  sessionId ? stream.sessionId === sessionId : stream.sessionId == null;

interface StreamsContextValue {
  getStream: (streamId: string) => StreamingState | undefined;
  getStreamForSession: (sessionId: string | undefined) => StreamingState | undefined;
  createStream: (state: StreamingState) => void;
  updateStream: (
    streamId: string,
    updater: (prev: StreamingState) => StreamingState,
  ) => void;
  abortStream: (streamId: string) => void;
  abortStreamsForSession: (sessionId: string | undefined) => void;
  removeStream: (streamId: string) => void;
  isSessionStreaming: (sessionId: string) => boolean;
}

const StreamsContext = createContext<StreamsContextValue | null>(null);

export const useStreams = (): StreamsContextValue => {
  const ctx = useContext(StreamsContext);
  if (!ctx) throw new Error("useStreams must be used within StreamsProvider");
  return ctx;
};

export const StreamsProvider = ({ children }: { children: ReactNode }) => {
  const [streams, setStreams] = useAtom(streamsAtom);

  const getStream = useCallback(
    (streamId: string) => streams.get(streamId),
    [streams],
  );

  const getStreamForSession = useCallback(
    (sessionId: string | undefined) => {
      const matches = Array.from(streams.values()).filter((s) =>
        streamMatchesSession(s, sessionId),
      );
      return (
        matches.find((s) => s.isStreaming) ??
        matches.find((s) => s.fadeHold) ??
        matches.at(-1)
      );
    },
    [streams],
  );

  const createStream = useCallback(
    (state: StreamingState) => {
      setStreams((prev) => {
        const next = new Map(prev);
        next.set(state.streamId, state);
        return next;
      });
    },
    [setStreams],
  );

  const updateStream = useCallback(
    (streamId: string, updater: (prev: StreamingState) => StreamingState) => {
      setStreams((prev) => {
        const existing = prev.get(streamId);
        if (!existing) return prev;
        const next = new Map(prev);
        next.set(streamId, updater(existing));
        return next;
      });
    },
    [setStreams],
  );

  const abortStream = useCallback(
    (streamId: string) => {
      setStreams((prev) => {
        const existing = prev.get(streamId);
        if (!existing) return prev;
        existing.abortController.abort();
        const next = new Map(prev);
        next.set(streamId, {
          ...existing,
          isStreaming: false,
          aborted: true,
          fadeHold: true,
        });
        return next;
      });
    },
    [setStreams],
  );

  const abortStreamsForSession = useCallback(
    (sessionId: string | undefined) => {
      setStreams((prev) => stopSessionStreams(prev, sessionId));
    },
    [setStreams],
  );

  const isSessionStreaming = useCallback(
    (sessionId: string) =>
      Array.from(streams.values()).some(
        (s) => s.isStreaming && s.sessionId === sessionId,
      ),
    [streams],
  );

  const removeStream = useCallback(
    (streamId: string) => {
      setStreams((prev) => {
        if (!prev.has(streamId)) return prev;
        const next = new Map(prev);
        next.delete(streamId);
        return next;
      });
    },
    [setStreams],
  );

  return (
    <StreamsContext.Provider
      value={{
        getStream,
        getStreamForSession,
        createStream,
        updateStream,
        abortStream,
        abortStreamsForSession,
        removeStream,
        isSessionStreaming,
      }}
    >
      {children}
    </StreamsContext.Provider>
  );
};
